import { Button, Spinner } from 'flowbite-react';
import React from 'react';
import { FaUser } from 'react-icons/fa6';
import { HiBriefcase, HiPhone } from 'react-icons/hi';
import { MdArrowRightAlt, MdEmail } from 'react-icons/md';
import { TbRadioactiveFilled } from 'react-icons/tb';
import { Img } from 'react-image';
import { LeadActionInput, MyCompanyType } from 'types';

interface CompanyCardFooterProps {
	company: MyCompanyType;
	onLeadAction: (data: LeadActionInput) => void;
	openLeadActionDrawer: () => void;
	isLoading?: boolean;
	handleViewCompanyDetails: (comp: MyCompanyType['company']) => void;
}

const AMLeadCompanyFooter = ({
	company,
	handleViewCompanyDetails,
	onLeadAction,
	isLoading,
	openLeadActionDrawer,
}: CompanyCardFooterProps) => {
	const onQualify = () => {
		onLeadAction({
			action: 'qualified',
			companyId: company.company._id,
			remarks: '',
		});
	};
	const onDisQualify = () => {
		openLeadActionDrawer();
	};

	// More defensive recruiter lookup with multiple fallback paths
	const recruiter = (() => {
		let foundRecruiter = null;

		// Try to find recruiter in company.recruiter array
		if (Array.isArray(company.company?.recruiter) && company.company.recruiter.length > 0) {
			foundRecruiter = company.company.recruiter.find(rec => rec.phone) || company.company.recruiter[0];
			if (foundRecruiter) return foundRecruiter;
		}

		// Try to find in leadGenMeta directly if it's an array
		const leadGenMeta = company.company?.leadGenMeta;
		if (Array.isArray(leadGenMeta) && leadGenMeta.length > 0) {

			// First try to get the leadGen object itself which should have name, email, phone
			for (const meta of leadGenMeta) {
				if (meta.leadGen && typeof meta.leadGen === 'object') {
					// If leadGen has direct properties for contact info, use those
					if (meta.leadGen.name || meta.leadGen.email || meta.leadGen.phone) {
						return {
							name: meta.leadGen.name || 'Not Available',
							email: meta.leadGen.email || '-',
							phone: meta.leadGen.phone || '-'
						};
					}
				}
			}
			// Check if any item in the array has a bdPOC or leadGenPOC
			// for (const meta of leadGenMeta) {
			// 	if (meta.leadGen?.bdPOC) {
			// 		return meta.leadGen.bdPOC;
			// 	}
			// 	if (meta.bdPOC) {
			// 		return meta.bdPOC;
			// 	}
			// 	if (meta.leadGen?.leadGenPOC) {
			// 		return meta.leadGen.leadGenPOC;
			// 	}
			// }
		}

		// Try the old structure as well (legacy support)
		if (leadGenMeta && Array.isArray(leadGenMeta.leadGen) && leadGenMeta.leadGen.length > 0) {
			foundRecruiter = leadGenMeta.leadGen.find(lead => lead.phone) || leadGenMeta.leadGen[0];
			if (foundRecruiter) return foundRecruiter;
		}

		// Return a placeholder object instead of throwing an error
		return {
			name: 'Not Available',
			phone: '-',
			email: '-'
		};
	})();


	return (
		<div className="flex flex-col gap-2">
			<div className="py-2 px-4 bg-white dark:bg-[#111E2C] flex items-center rounded-b-[12px]">
				<div className="bg-white dark:bg-[#111E2C] flex items-center grow-2">
					<div className="flex gap-y-2 gap-x-4 flex-col">
						<div className="flex flex-row gap-2 items-center">
							<div>
								<FaUser className="text-[#00B7C4] dark:text-[#A9EFF2]" />
							</div>
							<div className="flex gap-1 items-center sm:text-[12px] text-[14px] text-[#28293D] dark:text-[#E4E4EB] font-[500]">
								<div className="dark:text-[#E4E4EB] font-bold">Company POC Name:</div>
								<div className="text-sm text-[#28293D] dark:text-[#E4E4EB]">{recruiter && typeof recruiter === 'object' ? recruiter.name : '-'}</div>
							</div>
						</div>

						<div className="flex flex-row gap-2 items-center">
							<div>
								<HiPhone className="text-[#00B7C4] dark:text-[#A9EFF2]" />
							</div>
							<div className="flex gap-1 items-center sm:text-[12px] text-[14px] text-[#28293D] dark:text-[#E4E4EB] font-[500]">
								<div className="dark:text-[#E4E4EB] font-bold">Contact Number:</div>
								<div className="text-sm text-[#28293D] dark:text-[#E4E4EB]">{recruiter && typeof recruiter === 'object' && 'phone' in recruiter ? recruiter.phone : '-'}</div>
							</div>
						</div>
						<div className="flex flex-row gap-2 items-center">
							<div>
								<MdEmail className="text-[#00B7C4] dark:text-[#A9EFF2]" />
							</div>
							<div className="flex gap-1 items-center sm:text-[12px] text-[14px] text-[#28293D] dark:text-[#E4E4EB] font-[500]">
								<div className="dark:text-[#E4E4EB] font-bold">Email:</div>
								<div className="text-sm text-[#28293D] dark:text-[#E4E4EB]">{recruiter && typeof recruiter === 'object' && 'email' in recruiter ? recruiter.email : '-'}</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="flex flex-col justify-between py-2 px-4 border-t-[1px] border-t-[#C6C8D880]">
				<div className="flex gap-1 mt-2 mb-3">
					<p>Lead Status</p>
					<span className="text-red-600">*</span>
				</div>
				<div className="flex justify-between">
					<div className="flex gap-2">
						<Button color="purplePlaced" onClick={onQualify} disabled={isLoading}>
							{isLoading ? <Spinner /> : 'Qualify'}
						</Button>
						<Button
							color="whiteBlue"
							className="border-[1px] border-[#7777E7]"
							disabled={isLoading}
							onClick={onDisQualify}
						>
							Disqualify
						</Button>
					</div>
					<Button color="gray" onClick={() => handleViewCompanyDetails(company.company)}>
						<div className="flex gap-1 items-center">
							View Details <MdArrowRightAlt />
						</div>
					</Button>
				</div>
			</div>
		</div>
	);
};

const AMCompanyCardFooter = (props: CompanyCardFooterProps) => {
	const { company } = props;
	if (company?.company?.isLead) return <AMLeadCompanyFooter {...props} />;

	const activePositions = company?.activePositions;
	const allPositions = company?.totalPositions;
	return (
		<div className="py-2 px-4 bg-white dark:bg-[#111E2C] flex items-center rounded-b-[12px]">
			<div className="bg-white dark:bg-[#111E2C] flex items-center grow-2">
				<div className="flex gap-y-2 gap-x-4 flex-col">
					<div className="flex flex-row gap-2 items-center">
						<div>
							<HiBriefcase className="text-[#00B7C4] dark:text-[#A9EFF2]" />
						</div>
						<div className="flex gap-1 items-center sm:text-[12px] text-[14px] text-[#28293D] dark:text-[#E4E4EB] font-[500]">
							<div className="dark:text-[#E4E4EB]">Total Positions</div>
							<div className="font-inter font-[500] text-[18px] leading-[16px] text-[#28293D] dark:text-[#C7C9D9]">
								•
							</div>
							<div className="text-sm text-[#28293D] dark:text-[#E4E4EB]">
								{allPositions.toString().padStart(2, '0')}
							</div>
						</div>
					</div>

					<div className="flex flex-row gap-2 items-center">
						<div>
							<TbRadioactiveFilled className="text-[#00B7C4] dark:text-[#A9EFF2]" />
						</div>
						<div className="flex gap-1 items-center sm:text-[12px] text-[14px] text-[#28293D] dark:text-[#E4E4EB] font-[500]">
							<div className="dark:text-[#E4E4EB]">Active Positions</div>
							<div className="font-inter font-[500] text-[18px] leading-[16px] text-[#28293D] dark:text-[#C7C9D9]">
								•
							</div>
							<div className="text-sm text-[#28293D] dark:text-[#E4E4EB]">
								{activePositions.toString().padStart(2, '0')}
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="">
				<Img src={'/assets/chevronRightBlue.svg'} alt="chevronRightBlue" width={24} height={24} />
			</div>
		</div>
	);
};

export default AMCompanyCardFooter;
